import * as bootstrap from 'bootstrap'

/**
 * Initialise une validation HTML5 “vanilla JS” sur un formulaire donné.
 *
 * - Empêche la soumission si des champs sont invalides.
 * - Affiche les messages d’erreur HTML5 dans les `.invalid-feedback`.
 * - Ajoute la classe Bootstrap `is-invalid` pour le style.
 * - Réinitialise automatiquement l’état des champs dès que l’utilisateur
 *   modifie un champ (input/change), permettant au rouge de disparaître.
 *
 * @param {string} formId - ID du formulaire à valider.
 * @param {Function|null} onSubmit - fonction de soumission personnalisée (ex : Marketo).
 *
 * @return {HTMLFormElement | null} Le formulaire
 */
export function initVanillaForm(formId, onSubmit = null) {
    const form = document.getElementById(formId);

    if (!form) {
        console.error(`tentative d'initialisation d'un formulaire inexistant : #${formId}`)
        return null;
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const fields = Array.from(form.querySelectorAll('input, select, textarea')).filter(field => !field.classList.contains('choices__input--cloned'));
        let firstInvalid = null;

        fields.forEach((field) => {
            const valid = vanillaValidateField(form, field);
            if (!valid && !firstInvalid) {
                firstInvalid = field;
            }
        });

        if (firstInvalid) {
            if (firstInvalid.classList.contains('choices__input')) {
                firstInvalid = firstInvalid.closest('.choices__inner').querySelector('input.choices__input--cloned');
            }
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.focus({ preventScroll: false });
        } else {
            if (onSubmit) {
                onSubmit(form);
            }
            else {
                form.submit();
            }
        }
        form.classList.add('was-validated');
    });

    function isValidatableField(field) {
        return field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement
    }

    form.addEventListener('input', (e) => {
        const field = e.target;
        if (isValidatableField(field)) {
            vanillaValidateField(form, field);
        }
    }, true); // Avec true, le handler sur le form s'exécutera avant celui sur le champ.

    form.addEventListener('change', (e) => {
        const field = e.target;
        if (isValidatableField(field)) {
            vanillaValidateField(form, field);
        }
    }, true);

    return form
}

/**
 * Initialise la validation en temps réel pour un champ de confirmation.
 *
 * Cette fonction compare le champ de confirmation avec le champ original (ex. mot de passe)
 * à chaque saisie (input) et définit un message d’erreur personnalisé si les valeurs
 * ne correspondent pas.
 *
 * Paramètres :
 * - originalFieldId : ID du champ original (mot de passe, email, etc.)
 * - copyFieldId     : ID du champ de confirmation
 * - customMessage   : Message d’erreur à afficher si les valeurs ne correspondent pas
 *
 * Comportement :
 * - Si les valeurs diffèrent, `setCustomValidity()` définit le message fourni dans
 *   `customMessage`, affiché lors de la soumission du formulaire.
 * - Si les valeurs correspondent, le champ est considéré comme valide.
 *
 * Exemple d’usage :
 * initFieldConfirmationValidation('password', 'password_confirmation', 'Les mots de passe doivent correspondre.');
 */
export function initFieldConfirmationValidation(originalFieldId, copyFieldId, customMessage = 'Les valeurs des champs ne correspondent pas.') {
    const originalInput = document.getElementById(originalFieldId);
    const confirmInput = document.getElementById(copyFieldId);

    if (!originalInput || !confirmInput) {
        console.warn(`⚠️ Impossible d’initialiser la validation : élément #${originalFieldId} ou #${copyFieldId} introuvable.`);
        return;
    }

    const validate = () => {
        const mismatch = confirmInput.value !== originalInput.value;
        confirmInput.setCustomValidity(mismatch ? customMessage : '');
        reValidateField(confirmInput);
    };

    originalInput.addEventListener('input', validate);
    confirmInput.addEventListener('input', validate);
}

/**
 * Initialise une validation personnalisée pour une checkbox obligatoire.
 *
 * - Utilise l’attribut `title` de la checkbox comme message d’erreur personnalisé.
 * - Si la case est décochée au moment de la soumission du formulaire,
 *   le navigateur affichera ce message à la place de celui par défaut.
 * - Dès que l’utilisateur interagit avec la case (ex. coche/décoche),
 *   on réinitialise le message d’erreur.
 *
 * @param {string} checkboxId - L’ID de la checkbox à valider.
 */
export function initRequiredCheckboxValidation(checkboxId) {
    const checkbox = document.getElementById(checkboxId);

    if (!checkbox || !checkbox.title) {
        console.warn(`⚠️ Impossible d’initialiser la vérification de la checkbox : élément #${checkboxId} introuvable ou title manquant.`);
        return;
    }

    checkbox.addEventListener("invalid", () => {
        checkbox.setCustomValidity(checkbox.title);
    });

    checkbox.addEventListener("input", () => {
        checkbox.setCustomValidity("");
    });
}

/**
 * Ajoute la possibilité de révéler ou masquer la valeur d'un input email/mot de passe
 * au clic sur une icône à côté du champ.
 *
 * @param {string} inputId - ID du champ input
 */
export function initRevealInputOnClick(inputId) {
    const input = document.getElementById(inputId);
    if (!input) {
        console.warn(`⚠️ Impossible d’initialiser le toggle de la visibilité des inputs, élément #${inputId} introuvable.`);
        return;
    }

    const icon = input.closest('.input-group')?.querySelector('.input-group-text');
    if (!icon) {
        console.warn(`⚠️ Impossible d’initialiser le toggle de la visibilité des inputs, élément ${iconSelector} introuvable.`);
        return;
    }

    icon.addEventListener('click', () => {
        if (input.type === 'password') {
            input.type = 'text';
        } else {
            input.type = 'password';
        }
    });
}

/**
 * /**
 *  * Active ou désactive dynamiquement un champ en fonction de la valeur d'un autre champ.
 *  *
 *  * - Si la valeur sélectionnée dans le champ "source" correspond à `triggerValue`, le champ "cible" est activé.
 *  * - Sinon, le champ "cible" est désactivé et vidé.
 *  *
 *  * @param {string} sourceId - ID du champ dont la valeur est surveillée.
 *  * @param {string} targetId - ID du champ à activer/désactiver.
 *  * @param {string} triggerValue - Valeur qui déclenche l’activation du champ cible.
 *  */
export function toggleFieldBasedOnValue(sourceId, targetId, triggerValue) {
    const sourceField = document.getElementById(sourceId);
    const targetField = document.getElementById(targetId);

    if (!sourceField || !targetField) {
        console.warn(`⚠️ Impossible d’initialiser le listener : élément #${sourceId} ou #${targetId} introuvable.`);
        return;
    }

    sourceField.addEventListener('change', function () {
        targetField.disabled = sourceField.value !== triggerValue;

        // Si le champ est désactivé, on le vide pour éviter d’envoyer une ancienne valeur
        if (targetField.disabled) {
            targetField.value = '';
        }
    })
}

export function toggleTargetFieldDisabledStateBasedOnOtherFieldValue(sourceField, targetField, sourceValue) {
    if (!sourceField || !targetField) {
        return;
    }

    sourceField.addEventListener('change', () => {
        targetField.disabled = sourceField.value !== sourceValue;

        // Si le champ est désactivé, on le vide pour éviter d’envoyer une ancienne valeur
        if (targetField.disabled) {
            targetField.value = '';
        }
    })
}

/**
 * Initialise le switch custom et ses labels associés
 * @param {string} id
 * @param {function} callback
 */
export function initCustomSwitch(id, callback = () => null) {
    const customSwitch = document.getElementById(id);
    if (!customSwitch) {
        console.warn(`⚠️ Impossible d’initialiser le switch : élément ${id} introuvable.`);
        return;
    }

    const wrapper = document.getElementById(`${id}Wrapper`);
    if (!wrapper) {
        console.warn('⚠️ Wrapper du switch introuvable.');
        return;
    }

    const noLabel = wrapper.querySelector('.noLabel');
    const yesLabel = wrapper.querySelector('.yesLabel');
    const changeEvent = new Event('change');

    customSwitch.addEventListener('change', (e) => {
        callback(e.target.checked);
        yesLabel.classList.remove('subtitle1');
        yesLabel.classList.add('body1');
        noLabel.classList.remove('subtitle1');
        noLabel.classList.add('body1');
        if (e.target.checked) {
            yesLabel.classList.remove('body1');
            yesLabel.classList.add('subtitle1');
        } else {
            noLabel.classList.remove('body1');
            noLabel.classList.add('subtitle1');

        }
    });

    // Gestion clic sur label "Non"
    if (noLabel) {
        noLabel.addEventListener('click', () => {
            customSwitch.checked = false;
            customSwitch.dispatchEvent(changeEvent);
        });
    }

    // Gestion clic sur label "Oui"
    if (yesLabel) {
        yesLabel.addEventListener('click', () => {
            customSwitch.checked = true;
            customSwitch.dispatchEvent(changeEvent);
        });
    }
}

/**
 * Met à jour l'état de validation d'un champ de formulaire
 *
 * @param {string} inputId - L'id de l'input'
 * @param {boolean} isValid - Si le champ est valide
 * @param {string} message - Le message d'erreur (vide si valide)
 */
export function updateFieldValidation(inputId, isValid, message = '') {
    const input = document.getElementById(inputId);
    if (!input) {
        console.warn(`⚠️ Impossible de mettre à jour l'input : élément ${inputId} introuvable.`);
        return;
    }

    const container = input.closest('.input-group');
    const feedback = container?.querySelector('.invalid-feedback');

    if (isValid) {
        input.classList.remove('is-invalid');
        input.setCustomValidity('');
        if (feedback) {
            feedback.textContent = '';
            feedback.classList.remove('visible');
        }
    } else {
        input.classList.add('is-invalid');
        input.setCustomValidity(message);
        if (feedback) {
            feedback.textContent = message;
            feedback.classList.add('visible');
        }
    }
}

/**
 * Affiche ou masque une alerte collapse lorsque l'utilisateur saisit un numéro de compte DHL.
 * @param {string} fieldId - ID de l'input à surveiller
 */
export function initAccountNumberAlert(fieldId) {
    const input = document.getElementById(fieldId);

    if (!input) {
        console.warn(`⚠️ Impossible d’initialiser la vérification du pattern : élément #${fieldId} introuvable.`);
        return;
    }

    input.addEventListener('input', () => {
        const value = input.value;
        const alertEl = document.querySelector('.my-dhl-plus-alert');

        if (!alertEl) {
            console.warn(`⚠️ Impossible d’afficher l'alerte : élément #${fieldId} introuvable.`);
            return;
        }

        const bsCollapse = new bootstrap.Collapse(alertEl, { toggle: false });

        if (/^[0-9]{1,10}$/.test(value)) {
            bsCollapse.show();
        } else {
            bsCollapse.hide();
        }
    });
}

function getCustomMessage(field) {
    if (field.dataset.errorMessages) {
        const validityTypeValues = [
            'valueMissing',
            'typeMismatch',
            'patternMismatch',
            'tooLong',
            'tooShort',
            'rangeUnderflow',
            'rangeOverflow',
            'stepMismatch',
            'badInput',
            'customError'
        ]

        const validityType = validityTypeValues.find(key => field.validity[key] === true);
        try {
            const messages = JSON.parse(field.dataset.errorMessages);

            return messages[validityType] || field.validationMessage;
        } catch (e) {
            console.warn('Impossible de parser data-error-messages', e);
        }
    }
    return field.validationMessage;
}

function vanillaValidateField(form, field) {
    const choicesContainer = field.closest('.choices')
    if (!choicesContainer) {
        // On boucle pour la validation des radios group
        const inputs = form.querySelectorAll(`[name="${field.name}"]`);
        inputs.forEach(r => {
            const feedback  = r.parentElement.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.innerHTML = '';
            }
            r.classList.remove('is-invalid');
        });

        if (!field.checkValidity()) {
            const feedback = field.parentElement.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.textContent = getCustomMessage(field);
            }
            field.classList.add('is-invalid');
            return false;
        }

        inputs.forEach(r => r.classList.add('is-valid'));
    } else {
        choicesContainer.classList.remove('is-invalid')

        if (!field.checkValidity()) {
            const feedback = field.closest('.choices').parentElement.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.textContent = getCustomMessage(field);
            }

            choicesContainer.classList.add('is-invalid');

            return false;
        }
        choicesContainer.classList.add('is-valid');
    }

    return true;
}

export function forceFieldValidation(field, isValid, customMessage)  {
    const choicesContainer = field.closest('.choices');
    if (!choicesContainer) {
        field.classList.remove('is-invalid')

        if (!isValid) {
            const feedback = field.parentElement.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.textContent = customMessage ?? getCustomMessage(field);

            }
            field.classList.add('is-invalid');
            return false;
        }
    } else {
        choicesContainer.classList.remove('is-invalid')

        if (!isValid) {
            const feedback = field.closest('.choices').parentElement.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.textContent = customMessage ?? getCustomMessage(field);
            }
            choicesContainer.classList.add('is-invalid');
            return false;
        }
    }

    return true;
}

export function reValidateField(field)  {
    field.classList.remove('is-invalid')

    if (!field.checkValidity()) {
        const feedback = field.parentElement.querySelector('.invalid-feedback');
        if (feedback) {
            feedback.textContent = getCustomMessage(field);
        }

        field.classList.add('is-invalid');

        return false;
    }

    field.classList.add('is-valid');
    return true;
}
